import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
} from "react";
import { cn } from "@/lib/cn";

type Variant = "primary" | "secondary" | "ghost" | "danger";
const VARIANTS: Record<Variant, string> = {
  primary:
    "bg-accent text-white border-accent hover:bg-accent-2 hover:border-accent-2",
  secondary: "bg-surface text-ink border-border hover:bg-surface-2",
  ghost: "bg-transparent text-ink border-transparent hover:bg-surface-2",
  danger: "bg-transparent text-danger border-danger/40 hover:bg-danger-soft",
};

export function Button({
  children,
  variant = "secondary",
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  children: ReactNode;
  variant?: Variant;
}) {
  return (
    <button
      type="button"
      {...rest}
      className={cn(
        "inline-flex h-10 items-center justify-center gap-1.5 rounded-control border px-4 text-[14px] font-semibold transition-colors",
        "disabled:pointer-events-none disabled:opacity-45",
        VARIANTS[variant],
        className,
      )}
    >
      {children}
    </button>
  );
}

export function Input({
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...rest} className={cn("field", className)} />;
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: the control is `children`, always an input or select.
    <label className="flex flex-col gap-1.5">
      <span className="eyebrow">{label}</span>
      {children}
      {hint ? <span className="text-[12px] text-muted">{hint}</span> : null}
    </label>
  );
}

export function Card({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cn(
        "rounded-panel border border-border bg-surface p-6",
        className,
      )}
    >
      {children}
    </section>
  );
}

export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <header className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 className="text-[22px] font-bold tracking-[-0.02em]">{title}</h1>
        {subtitle ? (
          <p className="mt-1 text-[13px] text-muted">{subtitle}</p>
        ) : null}
      </div>
      {action}
    </header>
  );
}

export function ErrorNote({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p className="rounded-control border border-danger/25 bg-danger-soft px-3 py-2 text-[13px] text-danger">
      {message}
    </p>
  );
}

const TONES: Record<string, string> = {
  pending: "bg-surface-2 text-muted",
  uploaded: "bg-surface-2 text-muted",
  queued: "bg-accent-soft text-accent",
  discovering: "bg-accent-soft text-accent",
  processing: "bg-accent-soft text-accent",
  indexing: "bg-accent-soft text-accent",
  ready: "bg-success/10 text-success",
  failed: "bg-danger-soft text-danger",
};

export function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={cn(
        "inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold",
        TONES[status] ?? TONES.pending,
      )}
    >
      {status}
    </span>
  );
}

export function EmptyState({
  title,
  hint,
}: {
  title: string;
  hint?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-1 px-6 py-12 text-center">
      <p className="text-[14px] font-medium">{title}</p>
      {hint ? (
        <p className="max-w-[46ch] text-[13px] text-muted">{hint}</p>
      ) : null}
    </div>
  );
}
